import { Fragment, useEffect, useMemo, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  SearchIcon,
  UserCircleIcon,
  AdjustmentsIcon,
  ArrowRightIcon,
} from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Pagination from "react-js-pagination";

import Loader from "../../components/loader/Loader";
import * as api from "./apiService";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const roleOptions = [
  { id: "All", name: "All Roles" },
  { id: "Customers", name: "Customers" },
  { id: "Project Managers", name: "Project Managers" },
  { id: "Account Managers", name: "Account Managers" },
  { id: "Internal Coordinators", name: "Internal Coordinators" },
  { id: "Admins", name: "Admins" },
];

const defaultOption = { id: null, name: "All" };

const MetricCard = ({ label, value, subtext }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm min-w-0">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">
      {label}
    </div>
    <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
    {subtext ? (
      <div className="mt-1 text-sm text-slate-500 whitespace-nowrap">
        {subtext}
      </div>
    ) : null}
  </div>
);

const FilterableListbox = ({
  value,
  onChange,
  options,
  placeholder,
  searchValue,
  onSearchChange,
}) => {
  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            className="relative w-full rounded-xl border border-slate-300 bg-white py-3 pl-4 pr-10
                       text-left text-sm text-slate-900 shadow-sm transition
                       focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
          >
            <span className="block truncate">{value?.name || placeholder}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDownIcon
                className="h-5 w-5 text-slate-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className="absolute z-20 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-slate-200
                         bg-white py-2 shadow-xl focus:outline-none"
            >
              {onSearchChange ? (
                <div className="sticky top-0 z-10 bg-white px-2 pb-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchValue}
                      onChange={(e) => onSearchChange(e.target.value)}
                      placeholder={`Search ${placeholder.toLowerCase()}...`}
                      className="w-full rounded-xl border border-slate-300 bg-slate-50 py-2.5 pl-3 pr-10
                                 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    />
                    <SearchIcon className="pointer-events-none absolute right-3 top-2.5 h-5 w-5 text-slate-400" />
                  </div>
                </div>
              ) : null}

              {options.map((option) => (
                <Listbox.Option
                  key={`${placeholder}-${option.id ?? "all"}-${option.name}`}
                  value={option}
                  className={({ active }) =>
                    classNames(
                      active ? "bg-red-600 text-white" : "text-slate-900",
                      "relative cursor-pointer select-none py-2.5 pl-4 pr-10 text-sm",
                    )
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={classNames(
                          selected ? "font-semibold" : "font-normal",
                          "block truncate",
                        )}
                      >
                        {option.name}
                      </span>
                      {selected ? (
                        <span
                          className={classNames(
                            active ? "text-white" : "text-red-600",
                            "absolute inset-y-0 right-0 flex items-center pr-3",
                          )}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

const getRoleLabel = (user) => {
  if (user.is_staff || user.is_super_user || user.is_superuser) {
    return {
      text: "Admin",
      classes: "bg-green-100 text-green-700 ring-green-200",
    };
  }
  if (user.is_account_manager) {
    return {
      text: "Account Manager",
      classes: "bg-blue-100 text-blue-700 ring-blue-200",
    };
  }
  if (user.is_project_manager) {
    return {
      text: "Project Manager",
      classes: "bg-violet-100 text-violet-700 ring-violet-200",
    };
  }
  if (user.is_internal_coordinator) {
    return {
      text: "Coordinator",
      classes: "bg-orange-100 text-orange-700 ring-orange-200",
    };
  }
  if (user.customer_name) {
    return {
      text: "Customer",
      classes: "bg-sky-100 text-sky-700 ring-sky-200",
    };
  }
  return {
    text: "User",
    classes: "bg-slate-100 text-slate-700 ring-slate-200",
  };
};

const Users = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);

  const [searchText, setSearchText] = useState("");
  const [roleSelected, setRoleSelected] = useState(roleOptions[0]);

  const [customers, setCustomers] = useState([defaultOption]);
  const [customerSelected, setCustomerSelected] = useState(defaultOption);
  const [customerSearchName, setCustomerSearchName] = useState("");

  const [vendors, setVendors] = useState([defaultOption]);
  const [vendorSelected, setVendorSelected] = useState(defaultOption);
  const [vendorSearchName, setVendorSearchName] = useState("");

  useEffect(() => {
    const timeoutID = setTimeout(() => {
      searchUsers();
    }, 400);

    return () => clearTimeout(timeoutID);
  }, [searchText, roleSelected, customerSelected, vendorSelected, currentPage]);

  useEffect(() => {
    const timeoutID = setTimeout(() => {
      searchCustomers();
    }, 400);

    return () => clearTimeout(timeoutID);
  }, [customerSearchName]);

  useEffect(() => {
    const timeoutID = setTimeout(() => {
      searchVendors();
    }, 400);

    return () => clearTimeout(timeoutID);
  }, [vendorSearchName]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const searchCustomers = async () => {
    try {
      const request = { name: customerSearchName };
      const { data } = await api.getCustomers(request);
      setCustomers([defaultOption, ...(data.results || [])]);
    } catch (err) {
      toast.error("Unable to search customers");
    }
  };

  const searchVendors = async () => {
    try {
      const request = { name: vendorSearchName };
      const { data } = await api.searchVendors(request);
      setVendors([defaultOption, ...(data.results || [])]);
    } catch (err) {
      toast.error("Unable to search vendors");
    }
  };

  const searchUsers = async () => {
    setLoading(true);

    const request = {
      name: searchText,
      role: roleSelected.id,
      customer_id: customerSelected?.id ?? null,
      vendor_id: vendorSelected?.id ?? null,
    };

    try {
      const { data } = await api.searchUsers(request, currentPage);
      setUsers(data.results || []);
      setTotalUsers(data.count || 0);
    } catch (err) {
      toast.error("Unable to search users");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchText("");
    setRoleSelected(roleOptions[0]);
    setCustomerSelected(defaultOption);
    setVendorSelected(defaultOption);
    setCustomerSearchName("");
    setVendorSearchName("");
  };

  const customerUsersCount = useMemo(
    () => users.filter((user) => !!user.customer_name).length,
    [users],
  );

  const adminUsersCount = useMemo(
    () =>
      users.filter(
        (user) => user.is_staff || user.is_super_user || user.is_superuser,
      ).length,
    [users],
  );

  return (
    <div className="mx-auto max-w-7xl px-4 pb-14 pt-4 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 py-7 shadow-xl sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-slate-200">
              Team Management
            </div>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white">
              Users
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300">
              Search, filter, and manage your team in a cleaner table-driven
              experience.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:min-w-[540px] lg:max-w-[620px] lg:flex-shrink-0">
            <MetricCard
              label="Total Results"
              value={totalUsers}
              subtext="Matching current filters"
            />
            <MetricCard
              label="Admins"
              value={adminUsersCount}
              subtext="In current results"
            />
            <MetricCard
              label="Customers"
              value={customerUsersCount}
              subtext="In current results"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-slate-100 p-2.5">
            <AdjustmentsIcon className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
            <p className="text-sm text-slate-500">
              Refine results by name, role, customer, or vendor.
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="xl:col-span-4">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Search user
            </label>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by name..."
                className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 shadow-sm
                           focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
              />
            </div>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Role
            </label>
            <FilterableListbox
              value={roleSelected}
              onChange={setRoleSelected}
              options={roleOptions}
              placeholder="Role"
            />
          </div>

          <div className="xl:col-span-3">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Customer
            </label>
            <FilterableListbox
              value={customerSelected}
              onChange={setCustomerSelected}
              options={customers}
              placeholder="Customers"
              searchValue={customerSearchName}
              onSearchChange={setCustomerSearchName}
            />
          </div>

          <div className="xl:col-span-3">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Vendor
            </label>
            <FilterableListbox
              value={vendorSelected}
              onChange={setVendorSelected}
              options={vendors}
              placeholder="Vendors"
              searchValue={vendorSearchName}
              onSearchChange={setVendorSearchName}
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={searchUsers}
            className="inline-flex items-center rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Users Directory
            </h2>
          </div>
          <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
            {totalUsers} result{totalUsers === 1 ? "" : "s"}
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-12">
            <Loader />
          </div>
        ) : users.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
              <UserCircleIcon className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              No users found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Try adjusting your search text or filters.
            </p>
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      User
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {users.map((user) => {
                    const role = getRoleLabel(user);

                    return (
                      <tr
                        key={user.id}
                        className="transition hover:bg-slate-50/80"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            {user.profile?.avatar ? (
                              <img
                                src={user.profile.avatar}
                                alt=""
                                className="h-11 w-11 rounded-full object-cover ring-2 ring-slate-100"
                              />
                            ) : (
                              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                <UserCircleIcon className="h-7 w-7" />
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-semibold text-slate-900">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-slate-500">
                                {user.username || "No username"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <span
                            className={classNames(
                              "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
                              role.classes,
                            )}
                          >
                            {role.text}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-sm text-slate-600">
                          {user.customer_name || "—"}
                        </td>

                        <td className="px-6 py-4 text-sm text-slate-600">
                          {user.email || "No email specified"}
                        </td>

                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => navigate(`/users/${user.id}`)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            Details
                            <ArrowRightIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-200 lg:hidden">
              {users.map((user) => {
                const role = getRoleLabel(user);

                return (
                  <div key={user.id} className="p-5">
                    <div className="flex items-start gap-4">
                      {user.profile?.avatar ? (
                        <img
                          src={user.profile.avatar}
                          alt=""
                          className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-100"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                          <UserCircleIcon className="h-7 w-7" />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="mt-1 truncate text-sm text-slate-500">
                          {user.email || "No email specified"}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span
                            className={classNames(
                              "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
                              role.classes,
                            )}
                          >
                            {role.text}
                          </span>

                          {user.customer_name ? (
                            <span className="text-xs text-slate-500">
                              {user.customer_name}
                            </span>
                          ) : null}
                        </div>

                        <button
                          type="button"
                          onClick={() => navigate(`/users/${user.id}`)}
                          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                          Details
                          <ArrowRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {!loading && totalUsers > 20 && (
              <div className="m-auto px-10 pr-20 flex pt-5 pb-10 justify-end text-right">
                <div>
                  <Pagination
                    innerClass="pagination pagination-custom"
                    activePage={currentPage}
                    hideDisabled
                    itemClass="page-item page-item-custom"
                    linkClass="page-link page-link-custom"
                    itemsCountPerPage={20}
                    totalItemsCount={totalUsers}
                    pageRangeDisplayed={3}
                    onChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
